import React, { useEffect, useMemo, useState } from "react";
import "../styles/AdminSettingsPage.css";
import { useI18n } from "../providers/I18nProvider";
import { apiClient } from "../services/apiClient";
import NoirAlert from "../components/NoirAlert";
import { TabBar } from "../components/ListToolbar";
import useUnsavedChanges from "../hooks/useUnsavedChanges";

// Alanlar string olarak tutulur: kullanıcı alanı silince sessizce 0 olmasın,
// "-" gibi ara girdiler yazılabilsin. Kaydederken aralık doğrulanır.
const FIELDS = {
  leave_request_past_days_allowed: { min: 0, max: 365, def: 15 },
  min_leave_deduct_days: { def: 0.5 },
  min_negative_leave_balance_days: { min: -365, max: 0, def: -5 },
  user_total_leave_quota_min: { min: -365, max: 0, def: -14 },
  travel_daily_amount_try: { min: 0, max: 1000000, def: 0 },
  travel_daily_amount_eur: { min: 0, max: 1000000, def: 0 },
};

const numOr = (v, def) => (Number.isFinite(Number(v)) && v !== null && v !== "" ? Number(v) : def);

const fromServer = (data) => ({
  leave_request_past_days_allowed: String(numOr(data.leave_request_past_days_allowed, 15)),
  annual_leave_saturday_deduction_enabled: Boolean(data.annual_leave_saturday_deduction_enabled),
  min_leave_deduct_days: String(numOr(data.min_leave_deduct_days, 0.5)),
  min_negative_leave_balance_days: String(numOr(data.min_negative_leave_balance_days, -5)),
  birthday_email_enabled:
    data.birthday_email_enabled === undefined ? true : Boolean(data.birthday_email_enabled),
  user_total_leave_quota_min: String(numOr(data.user_total_leave_quota_min, -14)),
  travel_daily_amount_try: String(numOr(data.travel_daily_amount_try, 0)),
  travel_daily_amount_eur: String(numOr(data.travel_daily_amount_eur, 0)),
});

// Modül seviyesinde: bileşen render içinde tanımlanırsa her tuşta yeniden oluşur ve odak kaybolur.
const NumberFieldBase = ({ form, errors, setField, name, label, hint, ...rest }) => (
  <label>
    {label}
    <input
      type="number"
      inputMode="decimal"
      value={form[name]}
      onChange={setField(name)}
      aria-invalid={Boolean(errors[name])}
      {...rest}
    />
    {errors[name] && (
      <div className="field-error" role="alert">
        {errors[name]}
      </div>
    )}
    {hint && <div className="muted small">{hint}</div>}
  </label>
);

const AdminSettingsPage = () => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [tab, setTab] = useState("leave"); // leave | travel | notifications
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [loadError, setLoadError] = useState("");

  const dirty = useMemo(
    () => form !== null && saved !== null && JSON.stringify(form) !== JSON.stringify(saved),
    [form, saved]
  );
  useUnsavedChanges(dirty);

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await apiClient.get("/admin/site-settings");
      const next = fromServer(res.data || {});
      setForm(next);
      setSaved(next);
    } catch (err) {
      setLoadError(err.response?.data?.msg || t("settings.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Her alan için anlık hata (boş/geçersiz/aralık dışı)
  const errors = useMemo(() => {
    const out = {};
    if (!form) return out;
    Object.entries(FIELDS).forEach(([key, rule]) => {
      const raw = String(form[key] ?? "").trim();
      const n = Number(raw);
      if (raw === "" || !Number.isFinite(n)) out[key] = t("adminSettings.errors.number");
      else if (rule.min !== undefined && n < rule.min)
        out[key] = t("adminSettings.errors.min", { min: rule.min });
      else if (rule.max !== undefined && n > rule.max)
        out[key] = t("adminSettings.errors.max", { max: rule.max });
    });
    return out;
  }, [form, t]);
  const hasErrors = Object.keys(errors).length > 0;
  const tabHasError = {
    leave: ["leave_request_past_days_allowed", "min_negative_leave_balance_days", "user_total_leave_quota_min"].some((k) => errors[k]),
    travel: ["travel_daily_amount_try", "travel_daily_amount_eur"].some((k) => errors[k]),
    notifications: false,
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (hasErrors) {
      setNotification({ message: t("adminSettings.fixErrors"), type: "error" });
      return;
    }
    setSaving(true);
    try {
      await apiClient.put("/admin/site-settings", {
        leave_request_past_days_allowed: Number(form.leave_request_past_days_allowed),
        annual_leave_saturday_deduction_enabled: form.annual_leave_saturday_deduction_enabled,
        min_leave_deduct_days: Number(form.min_leave_deduct_days),
        min_negative_leave_balance_days: Number(form.min_negative_leave_balance_days),
        birthday_email_enabled: form.birthday_email_enabled,
        user_total_leave_quota_min: Number(form.user_total_leave_quota_min),
        travel_daily_amount_try: Number(form.travel_daily_amount_try),
        travel_daily_amount_eur: Number(form.travel_daily_amount_eur),
      });
      setSaved(form);
      setNotification({ message: t("common.saved"), type: "success" });
    } catch (err) {
      setNotification({
        message: err.response?.data?.msg || t("settings.saveError"),
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-settings-page">{t("common.loading")}</div>;
  if (!form) {
    // İlk yükleme başarısız: hata + tekrar dene (aksi halde sayfa "Yükleniyor"da kalıyordu)
    return (
      <div className="admin-settings-page">
        <div className="settings-load-error" role="alert">
          <span>{loadError || t("settings.loadError")}</span>
          <button type="button" className="ghost" onClick={load}>
            {t("adminSettings.retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-settings-page">
      <NoirAlert
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />
      <div className="page-head">
        <div>
          <h2>{t("settings.title")}</h2>
          <p className="muted">{t("settings.subtitle")}</p>
        </div>
      </div>

      <TabBar
        value={tab}
        onChange={setTab}
        tabs={[
          { key: "leave", label: `${t("adminSettings.tabs.leave")}${tabHasError.leave ? " ⚠" : ""}` },
          { key: "travel", label: `${t("adminSettings.tabs.travel")}${tabHasError.travel ? " ⚠" : ""}` },
          { key: "notifications", label: t("adminSettings.tabs.notifications") },
        ]}
      />

      <div className="card form-card">
        <form onSubmit={handleSave} className="settings-form" noValidate>
          {tab === "leave" && (
            <>
              <NumberFieldBase
                form={form}
                errors={errors}
                setField={setField}
                name="leave_request_past_days_allowed"
                label={t("settings.leaveRequestPastDaysAllowed")}
                min="0"
                max="365"
              />

              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={form.annual_leave_saturday_deduction_enabled}
                  onChange={setField("annual_leave_saturday_deduction_enabled")}
                />
                <span>{t("settings.annualLeaveSaturdayDeductionEnabled")}</span>
              </label>

              <label>
                {t("settings.minLeaveDeductDays")}
                <select value={form.min_leave_deduct_days} onChange={setField("min_leave_deduct_days")}>
                  <option value="1">1</option>
                  <option value="0.5">0.5</option>
                  <option value="0.25">0.25</option>
                </select>
                <div className="muted small">{t("settings.minLeaveDeductDaysHint")}</div>
              </label>

              <NumberFieldBase
                form={form}
                errors={errors}
                setField={setField}
                name="min_negative_leave_balance_days"
                label={t("settings.minNegativeLeaveBalanceDays")}
                hint={t("settings.minNegativeLeaveBalanceDaysHint")}
                min="-365"
                max="0"
                step="0.25"
              />

              <NumberFieldBase
                form={form}
                errors={errors}
                setField={setField}
                name="user_total_leave_quota_min"
                label={t("settings.userTotalLeaveQuotaMin")}
                hint={t("settings.userTotalLeaveQuotaMinHint")}
                min="-365"
                max="0"
                step="0.5"
              />
            </>
          )}

          {tab === "travel" && (
            <>
              <NumberFieldBase
                form={form}
                errors={errors}
                setField={setField}
                name="travel_daily_amount_try"
                label={t("settings.travelDailyAmountTry")}
                hint={t("settings.travelDailyAmountTryHint")}
                min="0"
                max="1000000"
                step="0.01"
              />
              <NumberFieldBase
                form={form}
                errors={errors}
                setField={setField}
                name="travel_daily_amount_eur"
                label={t("settings.travelDailyAmountEur")}
                hint={t("settings.travelDailyAmountEurHint")}
                min="0"
                max="1000000"
                step="0.01"
              />
              <div className="muted small">{t("adminSettings.travelNote")}</div>
            </>
          )}

          {tab === "notifications" && (
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={form.birthday_email_enabled}
                onChange={setField("birthday_email_enabled")}
              />
              <span>{t("settings.birthdayEmailEnabled")}</span>
            </label>
          )}

          <div className="actions settings-actions">
            <button type="submit" className="primary" disabled={saving || !dirty}>
              {saving ? t("adminSettings.saving") : t("common.save")}
            </button>
            <button type="button" className="ghost" onClick={load} disabled={saving}>
              {t("settings.reload")}
            </button>
            {dirty && <span className="dirty-note">{t("adminSettings.unsaved")}</span>}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminSettingsPage;
